<template>
  <div class="flex flex-col gap-6">

    <!-- Header -->
    <AppPageHeader :title="t('departments.title')" :description="t('departments.description')">
      <template #actions>
        <AppButton @click="showAddModal = true">
          <Plus class="h-4 w-4 mr-2" />{{ t('departments.addDepartment') }}
        </AppButton>
      </template>
    </AppPageHeader>

    <!-- Filters -->
    <AppCard>
      <div class="p-4 flex flex-wrap gap-3 items-end">
        <div class="flex flex-col gap-1 flex-1 min-w-40">
          <label class="text-xs font-medium text-muted-foreground">{{ t('departments.search') }}</label>
          <div class="relative">
            <Search class="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input type="text" v-model="searchQuery" @input="debouncedSearch" :placeholder="t('departments.searchPlaceholder')" class="h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-muted-foreground">{{ t('departments.status') }}</label>
          <select v-model="activeFilter" @change="loadDepartments" class="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
            <option value="">{{ t('departments.allDepartments') }}</option>
            <option value="true">{{ t('departments.activeOnly') }}</option>
            <option value="false">{{ t('departments.inactiveOnly') }}</option>
          </select>
        </div>
        <AppButton variant="outline" @click="resetFilters">{{ t('departments.reset') }}</AppButton>
      </div>
    </AppCard>

    <!-- List -->
    <AppCard>
      <div class="px-4 py-3 border-b border-border flex items-center justify-between">
        <p class="text-sm font-medium text-foreground">Departments ({{ departments.length }})</p>
        <p class="text-xs text-muted-foreground">Page {{ pagination.page }} of {{ pagination.totalPages }}</p>
      </div>

      <div v-if="loading" class="flex items-center justify-center py-12"><AppSpinner size="lg" /></div>
      <AppEmptyState v-else-if="departments.length === 0" :icon="Building2" :title="t('departments.noDepartments')" :description="t('departments.noDepartmentsDesc')" />

      <div v-else class="divide-y divide-border">
        <div v-for="dept in departments" :key="dept.id" class="p-4 hover:bg-muted/30 transition-colors">
          <div class="flex items-start justify-between gap-4">
            <div class="flex-1 min-w-0">
              <div class="flex flex-wrap items-center gap-2 mb-2">
                <span class="text-sm font-semibold text-foreground">{{ dept.name }}</span>
                <span v-if="dept.code" class="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono">{{ dept.code }}</span>
                <AppBadge :variant="dept.isActive ? 'success' : 'destructive'">{{ dept.isActive ? t('departments.active') : t('departments.inactive') }}</AppBadge>
              </div>
              <p v-if="dept.description" class="text-xs text-muted-foreground mb-2">{{ dept.description }}</p>
              <div class="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div><p class="text-muted-foreground">{{ t('departments.manager') }}</p><p class="font-medium text-foreground">{{ dept.manager?.name || t('departments.notAssigned') }}</p></div>
                <div><p class="text-muted-foreground">{{ t('departments.employees') }}</p><p class="font-medium text-foreground">{{ dept.employeeCount }}</p></div>
                <div><p class="text-muted-foreground">{{ t('departments.budget') }}</p><p class="font-medium text-foreground">{{ dept.budget ? '$' + formatNumber(dept.budget) : t('departments.notSet') }}</p></div>
                <div><p class="text-muted-foreground">{{ t('departments.workingHours') }}</p><p class="font-medium text-foreground">{{ dept.workingHours?.name || t('departments.notConfigured') }}</p></div>
              </div>
              <p v-if="dept.email || dept.phone" class="text-xs text-muted-foreground mt-1">
                <span v-if="dept.email">{{ dept.email }}</span>
                <span v-if="dept.email && dept.phone"> · </span>
                <span v-if="dept.phone">{{ dept.phone }}</span>
              </p>
            </div>
            <div class="flex items-center gap-1 shrink-0">
              <AppButton size="icon-sm" variant="ghost" title="Edit" @click="editDepartment(dept)"><Edit class="h-3.5 w-3.5" /></AppButton>
              <AppButton size="icon-sm" variant="ghost" class="text-destructive" :title="dept.isActive ? t('departments.deactivate') : t('departments.delete')" @click="confirmDelete(dept)"><Trash2 class="h-3.5 w-3.5" /></AppButton>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.totalPages > 1" class="flex items-center justify-between px-4 py-3 border-t border-border">
        <span class="text-sm text-muted-foreground">{{ ((pagination.page-1)*pagination.limit)+1 }}–{{ Math.min(pagination.page*pagination.limit, pagination.total) }} of {{ pagination.total }}</span>
        <div class="flex items-center gap-1">
          <AppButton size="icon-sm" variant="outline" :disabled="pagination.page === 1" @click="changePage(pagination.page-1)"><ChevronLeft class="h-3.5 w-3.5" /></AppButton>
          <button v-for="p in getVisiblePages()" :key="p" @click="changePage(p)" :class="['h-8 min-w-[2rem] px-2 rounded-md text-sm font-medium transition-colors', p === pagination.page ? 'bg-primary text-primary-foreground' : 'border border-border text-foreground hover:bg-accent']">{{ p }}</button>
          <AppButton size="icon-sm" variant="outline" :disabled="pagination.page === pagination.totalPages" @click="changePage(pagination.page+1)"><ChevronRight class="h-3.5 w-3.5" /></AppButton>
        </div>
      </div>
    </AppCard>

    <!-- Add/Edit Modal -->
    <AppModal v-model="showDeptModal" :title="showEditModal ? t('departments.editDepartmentTitle') : t('departments.addDepartmentTitle')" size="lg">
      <form @submit.prevent="saveDepartment" class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">{{ t('departments.name') }} <span class="text-destructive">*</span></label>
          <input type="text" v-model="departmentForm.name" required placeholder="e.g., Human Resources" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">{{ t('departments.code') }}</label>
          <input type="text" v-model="departmentForm.code" placeholder="e.g., HR" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <div class="md:col-span-2 flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">{{ t('departments.descriptionLabel') }}</label>
          <textarea v-model="departmentForm.description" rows="2" placeholder="Department description…" class="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"></textarea>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">{{ t('departments.manager') }}</label>
          <select v-model="departmentForm.managerId" class="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
            <option value="">{{ t('departments.notAssigned') }}</option>
            <option v-for="m in availableManagers" :key="m.id" :value="m.id">{{ m.name }}</option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">{{ t('departments.budget') }}</label>
          <input type="number" v-model="departmentForm.budget" step="0.01" min="0" placeholder="0.00" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">{{ t('departments.email') }}</label>
          <input type="email" v-model="departmentForm.email" placeholder="department@company.com" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">{{ t('departments.phone') }}</label>
          <input type="tel" v-model="departmentForm.phone" placeholder="+1 (555) 123-4567" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">{{ t('departments.workingHours') }}</label>
          <select v-model="departmentForm.workingHoursConfigId" class="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
            <option value="">{{ t('departments.notConfigured') }}</option>
            <option v-for="c in workingHoursConfigs" :key="c.id" :value="c.id">{{ c.name }}</option>
          </select>
        </div>
        <div class="md:col-span-2">
          <label class="flex items-center gap-2 cursor-pointer text-sm">
            <input type="checkbox" v-model="departmentForm.isActive" class="rounded" />
            <span class="text-foreground">{{ t('departments.active') }}</span>
          </label>
        </div>
      </form>
      <template #footer>
        <div class="flex justify-end gap-2">
          <AppButton variant="outline" @click="closeModal">{{ t('departments.cancel') }}</AppButton>
          <AppButton :disabled="submitting" @click="saveDepartment">
            {{ submitting ? t('common.loading') : (showEditModal ? t('departments.editDepartmentTitle') : t('departments.addDepartmentTitle')) }}
          </AppButton>
        </div>
      </template>
    </AppModal>

    <!-- Delete confirmation modal -->
    <AppModal v-model="showDeleteModal" :title="t('departments.confirmDelete')" size="sm">
      <div class="flex gap-3">
        <AlertTriangle class="h-5 w-5 text-destructive shrink-0 mt-0.5" />
        <p class="text-sm text-muted-foreground">
          {{ departmentToDelete?.isActive ? t('departments.deactivate') : t('departments.delete') }}
          <strong class="text-foreground">{{ departmentToDelete?.name }}</strong>?
        </p>
      </div>
      <template #footer>
        <div class="flex justify-end gap-2">
          <AppButton variant="outline" @click="showDeleteModal = false">{{ t('departments.cancel') }}</AppButton>
          <AppButton variant="destructive" :disabled="deleting" @click="deleteDepartment">
            {{ deleting ? t('common.loading') : (departmentToDelete?.isActive ? t('departments.deactivate') : t('departments.delete')) }}
          </AppButton>
        </div>
      </template>
    </AppModal>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  Plus, Search, Building2, Edit, Trash2, AlertTriangle,
  ChevronLeft, ChevronRight
} from 'lucide-vue-next';

import { AppPageHeader, AppCard, AppBadge, AppButton, AppModal, AppEmptyState, AppSpinner } from '@/components/ui';

import adminDepartmentsService from '@/services/adminDepartmentsService';
import adminWorkingHoursService from '@/services/adminWorkingHoursService';
import adminService from '@/services/adminService';

const { t } = useI18n();
const loading = ref(false);
const submitting = ref(false);
const deleting = ref(false);
const departments = ref([]);
const availableManagers = ref([]);
const workingHoursConfigs = ref([]);

const searchQuery = ref('');
const activeFilter = ref('');
const pagination = ref({ page: 1, limit: 10, total: 0, totalPages: 0 });

const showAddModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const editingDepartment = ref(null);
const departmentToDelete = ref(null);

const departmentForm = ref({
  name: '', code: '', description: '', budget: null,
  email: '', phone: '', managerId: null, workingHoursConfigId: null, isActive: true
});

const showDeptModal = computed({
  get: () => showAddModal.value || showEditModal.value,
  set: (v) => { if (!v) closeModal(); }
});

const loadDepartments = async () => {
  try {
    loading.value = true;
    const params = { page: pagination.value.page, limit: pagination.value.limit, search: searchQuery.value, active: activeFilter.value };
    const response = await adminDepartmentsService.getDepartments(params);
    departments.value = response.data?.departments || [];
    if (response.data?.pagination) pagination.value = response.data.pagination;
  } catch (error) {
    console.error('Error loading departments:', error);
    departments.value = [];
  } finally {
    loading.value = false;
  }
};

const loadManagers = async () => {
  try {
    const response = await adminService.getUsers({ role: 'admin', limit: 100 });
    availableManagers.value = response.data?.users || [];
  } catch (error) {
    availableManagers.value = [];
  }
};

const loadWorkingHoursConfigs = async () => {
  try {
    const response = await adminWorkingHoursService.getWorkingHoursConfigs({ limit: 100 });
    workingHoursConfigs.value = response.data?.workingHours || [];
  } catch (error) {
    workingHoursConfigs.value = [];
  }
};

const debouncedSearch = debounce(() => { pagination.value.page = 1; loadDepartments(); }, 500);
const resetFilters = () => { searchQuery.value = ''; activeFilter.value = ''; pagination.value.page = 1; loadDepartments(); };
const changePage = (page) => { pagination.value.page = page; loadDepartments(); };

const getVisiblePages = () => {
  const current = pagination.value.page;
  const total = pagination.value.totalPages;
  const delta = 2;
  const range = [];
  for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) range.push(i);
  if (current - delta > 2) range.unshift('...');
  if (current + delta < total - 1) range.push('...');
  range.unshift(1);
  if (total > 1) range.push(total);
  return range.filter((page, index, array) => array.indexOf(page) === index);
};

const editDepartment = (department) => {
  editingDepartment.value = department;
  departmentForm.value = {
    name: department.name, code: department.code, description: department.description,
    budget: department.budget, email: department.email, phone: department.phone,
    managerId: department.manager?.id || null, workingHoursConfigId: department.workingHours?.id || null,
    isActive: department.isActive
  };
  showEditModal.value = true;
};

const confirmDelete = (department) => { departmentToDelete.value = department; showDeleteModal.value = true; };

const saveDepartment = async () => {
  try {
    submitting.value = true;
    if (showEditModal.value) {
      await adminDepartmentsService.updateDepartment(editingDepartment.value.id, departmentForm.value);
    } else {
      await adminDepartmentsService.createDepartment(departmentForm.value);
    }
    closeModal();
    await loadDepartments();
  } catch (error) {
    console.error('Error saving department:', error);
  } finally {
    submitting.value = false;
  }
};

const deleteDepartment = async () => {
  try {
    deleting.value = true;
    await adminDepartmentsService.deleteDepartment(departmentToDelete.value.id);
    showDeleteModal.value = false;
    departmentToDelete.value = null;
    await loadDepartments();
  } catch (error) {
    console.error('Error deleting department:', error);
  } finally {
    deleting.value = false;
  }
};

const closeModal = () => {
  showAddModal.value = false;
  showEditModal.value = false;
  editingDepartment.value = null;
  departmentForm.value = { name: '', code: '', description: '', budget: null, email: '', phone: '', managerId: null, workingHoursConfigId: null, isActive: true };
};

const formatNumber = (num) => new Intl.NumberFormat('en-US').format(num);

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => { clearTimeout(timeout); func(...args); };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

onMounted(() => { loadDepartments(); loadManagers(); loadWorkingHoursConfigs(); });
</script>
